/**
 * Shared row actions menu shell. Born in the documents migration
 * (insurescope-zkolv.3.2.3); promoted to components/shared when the admin
 * Users table became its second feature consumer. shadcn-locked: listed in
 * the eslint MUI-ban block, so never add an MUI import here.
 *
 * The 4 row action menus (Document, Folder, Scope, Comparison) share the same
 * chrome: a kebab trigger that opens a shadcn dropdown with 3-4 items, where
 * the trailing Delete is destructive and separated from the rest.
 *
 * The shell is parametrized by an `actions` array. The caller composes the
 * items + owns the dialog state. The shell handles:
 *   - DropdownMenu + Trigger + Content boilerplate
 *   - `stopPropagation` on the trigger so the row's onClick doesn't fire
 *   - Auto-separator BEFORE any destructive item (per design brief)
 *   - `data-variant="destructive"` via shadcn's built-in DropdownMenuItem variant
 *
 * Not parametrized: icons. Each call site passes its own `icon` ReactNode so
 * inline lucide imports stay readable per-menu instead of magicked behind an
 * enum.
 */
import { MoreVertical } from "lucide-react";
import * as React from "react";

import { buttonVariants } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface RowAction {
    key: string;
    label: string;
    icon?: React.ReactNode;
    onSelect: () => void;
    disabled?: boolean;
    variant?: "default" | "destructive";
}

interface RowActionsMenuProps {
    actions: RowAction[];
    ariaLabel?: string;
    disabled?: boolean;
}

export function RowActionsMenu({
    actions,
    ariaLabel = "Open actions menu",
    disabled,
}: RowActionsMenuProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                    buttonVariants({ variant: "ghost", size: "icon-sm" })
                )}
            >
                <MoreVertical size={16} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="min-w-36"
                onClick={(event) => event.stopPropagation()}
            >
                {actions.map((action, index) => {
                    const prev = actions[index - 1];
                    const needsSeparator =
                        action.variant === "destructive" &&
                        prev &&
                        prev.variant !== "destructive";
                    return (
                        <React.Fragment key={action.key}>
                            {needsSeparator && <DropdownMenuSeparator />}
                            <DropdownMenuItem
                                disabled={action.disabled}
                                variant={action.variant}
                                onClick={action.onSelect}
                            >
                                {action.icon}
                                {action.label}
                            </DropdownMenuItem>
                        </React.Fragment>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
